// demoHandoff.js - carries a trip from the website demo into the app.
//
// Added Oct 2026 (homepage demo, see FlyAtlast-Demo-Spec.md). The demo's
// "Pin this trip" button opens
//   https://app.flyatlast.com/login?signup=true&demo=1&trip=<base64url JSON>
// This module:
//   1. captures that trip the moment the app loads (index.js calls
//      captureDemoTripFromUrl() BEFORE React renders, so it survives the
//      redirect a logged-in user gets from /login to /dashboard),
//   2. keeps it in Preferences (same store as the pending referral code, so
//      it also survives sign-up -> email confirm -> log in), for 24 hours,
//   3. after login, App.js takes it and sends the user to the Pin Trip form
//      pre-filled. The pin is NEVER created automatically: the user taps
//      "Pin This Trip" themselves.
import { Preferences } from '@capacitor/preferences';
import { getPlaceSuggestions } from '../api/atlast';

const KEY = 'flyatlast_pending_demo_trip';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
const IATA = /^[A-Z]{3}$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

function decodeTrip(raw) {
  try {
    const b64 = raw.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    return JSON.parse(decodeURIComponent(escape(atob(padded))));
  } catch (e) {
    return null;
  }
}

// Returns the validated trip, or null. Safe to call on every page load.
export function captureDemoTripFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('demo') !== '1' || !params.get('trip')) return null;
    const t = decodeTrip(params.get('trip'));
    if (!t || !IATA.test(t.o) || !IATA.test(t.d) || !DATE.test(t.dep)) return null;
    if (t.ret && !DATE.test(t.ret)) return null;
    const budget = Number(t.b);
    if (!(budget > 0) || budget > 1000000) return null;
    if (!/^[A-Z]{3}$/.test(t.c)) return null;
    // Travellers (added with the multi-traveller free check). Older links have none: 1 adult.
    const adults = Number.isInteger(Number(t.a)) && Number(t.a) >= 1 && Number(t.a) <= 9 ? Number(t.a) : 1;
    const ch = Array.isArray(t.ch)
      ? t.ch.map(Number).filter((n) => Number.isInteger(n) && n >= 0 && n <= 17).slice(0, 8)
      : [];
    const kids = adults + ch.length > 9 ? [] : ch;
    const clean = { o: t.o, d: t.d, dep: t.dep, ret: t.ret || '', b: budget, c: t.c, a: adults, ch: kids };
    Preferences.set({ key: KEY, value: JSON.stringify({ ...clean, savedAt: Date.now() }) });
    return clean;
  } catch (e) {
    return null;
  }
}

// Reads AND removes the pending trip (so it only ever pre-fills once).
export async function takePendingDemoTrip() {
  try {
    const { value } = await Preferences.get({ key: KEY });
    if (!value) return null;
    await Preferences.remove({ key: KEY });
    const t = JSON.parse(value);
    if (!t || !t.savedAt || Date.now() - t.savedAt > MAX_AGE_MS) return null;
    return t;
  } catch (e) {
    return null;
  }
}

// Same display-label rule as AirportAutocomplete, so the pre-filled form
// looks exactly as if the user had picked the airports themselves.
function labelFor(place, code) {
  if (!place) return { name: code, label: code, country: null };
  const label = place.city_name && place.city_name !== place.name
    ? `${place.name} (${place.iata_code})`
    : `${place.name || place.city_name} (${place.iata_code})`;
  return { name: label.split(' (')[0], label, country: place.iata_country_code || null };
}

async function lookup(code) {
  try {
    const list = await getPlaceSuggestions(code);
    const arr = Array.isArray(list) ? list : (list && (list.suggestions || list.results)) || [];
    return arr.find((p) => p.iata_code === code && p.type === 'airport') || arr.find((p) => p.iata_code === code) || null;
  } catch (e) {
    return null;
  }
}

// Turns the stored trip into the exact fields PinTrip.js needs.
export async function resolveDemoTrip(t) {
  const [from, to] = await Promise.all([lookup(t.o), lookup(t.d)]);
  const o = labelFor(from, t.o);
  const d = labelFor(to, t.d);
  return {
    origin_code: t.o,
    origin_display: o.label,
    origin_country_code: o.country,
    destination_code: t.d,
    destination_name: d.name,
    destination_display: d.label,
    destination_country_code: d.country,
    departure_date: t.dep,
    return_date: t.ret || '',
    budget: t.b,
    currency: t.c,
    adults: t.a || 1,
    child_ages: Array.isArray(t.ch) ? t.ch : [],
  };
}
